import { rawModes } from '../constants/constants';

export interface RawModeOptionWithDisabled {
    name: string;
    abbreviation: string;
    help: string;
    isBatched: boolean;
    refKeyRequired: boolean;
    disabled: boolean;
}

export function isBatchedRawMode(rawMode: string | null): boolean {
    const matchingRawMode = Object.values(rawModes).find(mode => mode.name === rawMode && mode.isBatched);
    return matchingRawMode !== undefined;
}

export function isFullLoadRawMode(rawMode: string | null): boolean {
    // TODO: maybe rename this function to isCompleteLoadRawMode to avoid confusion with the Full
    const matchingRawMode = Object.values(rawModes).find(mode => mode.name === rawMode && !mode.isBatched);
    return matchingRawMode !== undefined;
}

// Gets the full list of raw mode options with optional file source type filtering
export function getRawModeOptions(isFileSourceType: boolean = false): RawModeOptionWithDisabled[] {
    let options = Object.values(rawModes).map(option => ({ ...option, disabled: false }));

    if (isFileSourceType) {
        options = options.filter(option => option.name !== rawModes.SyncDeletes.name);
    }

    return options;
}

// Gets raw mode options excluding EntityMigration
export function getRawModeOptionsWithoutEntityMigration(
    isFileSourceType: boolean = false
): RawModeOptionWithDisabled[] {
    return getRawModeOptions(isFileSourceType).filter(option => option.name !== rawModes.EntityMigration.name);
}

export function filterCsaAndCdcOptionsByFeatureFlag(
    options: RawModeOptionWithDisabled[],
    isIncrementalCdcEnabled: boolean = false
): RawModeOptionWithDisabled[] {
    const hiddenRawMode = isIncrementalCdcEnabled ? rawModes.Incremental_CSA.name : rawModes.Incremental_CDC.name;
    return options.filter(option => option.name !== hiddenRawMode);
}

// Gets selectable raw mode options excluding EntityMigration, showing CSA or CDC based on the feature flag
export function getSelectableRawModeOptions(
    isFileSourceType: boolean = false,
    isIncrementalCdcEnabled: boolean = false
): RawModeOptionWithDisabled[] {
    return filterCsaAndCdcOptionsByFeatureFlag(
        getRawModeOptionsWithoutEntityMigration(isFileSourceType),
        isIncrementalCdcEnabled
    );
}

// EntityMigration should only be available if it is already selected
export function shouldShowEntityMigrationForJob(currentJobRawMode: string | null): boolean {
    return currentJobRawMode === rawModes.EntityMigration.name;
}

// Incremental_CSA is no longer selectable for new jobs, but existing CSA jobs should still render/edit normally
export function shouldShowCsaForJob(currentJobRawMode: string | null): boolean {
    return currentJobRawMode === rawModes.Incremental_CSA.name;
}

// Incremental_CDC reuses Incremental_CSA's behavior/defaults; CSA is being phased out in favor of CDC.
// Centralized here so CSA removal later only requires updating this one function.
export function isCsaOrCdc(rawMode: string | null): boolean {
    return rawMode === rawModes.Incremental_CSA.name || rawMode === rawModes.Incremental_CDC.name;
}
